import {
  AuthenticatedUser,
  IResource,
  Office,
  ResourceType
} from "@gettactic/api";
import { canScheduleOthers } from "../permissions";
import { resourceDedicatedExceptions } from "./dedicatedSeats";
import { getReservationExtensionDays } from "./defines";
import { isAllowedToReserve } from "./isAllowedToReserve";

export interface IsResourceAvailableOptions {
  authenticatedUser: any;
  officeTimeZone: string;
  office: Office;
  resourceType: ResourceType;
  allowReserveOnBehalf: boolean;
  meetingsDisabled: boolean;
  startDate: Date;
}

export const isResourceAvailable = (
  authenticatedUser: AuthenticatedUser,
  resource: IResource,
  options?: IsResourceAvailableOptions
) => {
  const allowReserveOnBehalf = canScheduleOthers(authenticatedUser);
  const { meetingsDisabled, resourceType, office, officeTimeZone, startDate } =
    options || {};
  if (!resourceType || !startDate) return false;
  const allowReserveByMaxDate = isAllowedToReserve(
    startDate,
    officeTimeZone || "",
    office,
    resourceType,
    authenticatedUser.user?.organization_user?.roles,
    getReservationExtensionDays(authenticatedUser.user?.email)
  );

  if (!resource || !resource.is_available) {
    return false;
  }
  // A dedicated resource belongs to its owners and is not bookable — not even
  // on behalf of someone else, so this has to come before the
  // reserve-on-behalf escape hatch below. The exception: the days an owner
  // released their seat, which the resource carries when it is looked at on one.
  if (
    resource.is_dedicated &&
    resourceDedicatedExceptions(resource).length === 0
  ) {
    return false;
  }
  // If the user can reserve on behalf of others, we need to be able to trigger the reserve modal
  // with the resource pre-selected.
  if (allowReserveOnBehalf) {
    return true;
  }

  return (
    allowReserveByMaxDate &&
    resource.available_slots > 0 &&
    (resource.type !== "meeting_room" || !meetingsDisabled)
  );
};
